/**
 * A property the viewer's plan does not unlock.
 *
 * The server decides lock state and sends this shape instead of a Property —
 * it is NOT a Property with fields blanked out. There is no street address,
 * no exact value, and the coordinates are deliberately offset from the real
 * parcel, so nothing here can be un-blurred client-side.
 *
 * Fields absent by design: address, parcelId, assessedValue, estMarketValue,
 * amountDue, leadSignals, auctionDate.
 */
export interface LockedProperty {
  id: string;
  countyId: string;
  /** City + zip only — the street address is never sent for a locked row. */
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  status: string;
  inAuction: boolean;
  /** A wide range like "$250K–$500K", never a number. Absent when unknown. */
  valueBucket?: string;
  /** JITTERED ~250-450m from the true parcel. Never plot these as exact. */
  longitude: number | null;
  latitude: number | null;
  locked: true;
}

/** A row from /properties, which now returns both shapes in one array. */
export type PropertyRow = Property | LockedProperty;

/**
 * Narrows a row to LockedProperty.
 *
 * Discriminates on the server-set `locked` flag rather than sniffing for
 * missing fields — a client-side guess about lock state is how cosmetic-only
 * gates get built. The server decides; the client renders.
 */
export function isLockedProperty(row: PropertyRow): row is LockedProperty {
  return (row as LockedProperty).locked === true;
}

/**
 * One UNLOCKED property as a map pin — the unlocked-row shape of
 * GET /properties?fields=map (backend handlers.MapPoint).
 *
 * Deliberately a structural SUBSET of Property: every full Property is a valid
 * MapPoint, so the map draws from either. It is NOT a property — it has no
 * address, parcel id, value or owner, and must never be passed where a
 * Property is expected (use isFullProperty to narrow).
 */
export interface MapPoint {
  id: string;
  countyId: string;
  status: string;
  zipCode: string;
  longitude: number | null;
  latitude: number | null;
  /** Only upsetWindowCloseAt is sent; resolveDisplayStatus reads it. */
  sale?: { upsetWindowCloseAt?: string };
}

/**
 * Narrows a pin to a full Property. Every Property row carries a (possibly
 * empty) parcelId string; a MapPoint never has the key.
 */
export function isFullProperty(p: MapPoint | Property): p is Property {
  return "parcelId" in p;
}

export interface Property {
  id: string;
  countyId: string;
  parcelId: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  propertyType: string;
  propertyClass: string;
  assessedValue: number;
  amountDue: number;
  // Estimated market value from county CAMA enrichment (#445), written by the
  // nightly cmd/spatialest-enrich job. NULL for the majority of rows: only
  // CAMA-wired counties produce it, and the enrichment covers live-stage
  // properties on a 30-day TTL. Prefer resolveMarketValue() in lib/dataQuality
  // over reading this directly — it falls back to the county assessed value
  // (present on ~84% of near-term auctions vs ~12% for this field) and labels
  // which one a viewer is looking at.
  //
  // Zeroed by the API for free/out-of-plan viewers (viewProperty's !allowed
  // branch; the redactPropertyForPlan this comment named was replaced by the
  // PropertyView allowlist in #1872 / ADR 0035), so a null here can mean "not
  // enriched" OR "not entitled" — never infer a data gap from a null alone on
  // a gated view.
  estMarketValue?: number | null;
  estMarketValueSource?: string;
  estMarketValueUpdatedAt?: string | null;
  auctionDate: string | null;
  status: string;
  bpoUrl: string;
  longitude: number | null;
  latitude: number | null;
  // Best available street-level photo (from property_images, #583). Null when
  // no Street View / Mapillary coverage exists for the parcel — PhotoGallery
  // then falls back to the county BPO photo or an aerial tile.
  primaryImage?: PropertyImage | null;
  // true = a live auction (status auction_scheduled/pending_confirmation);
  // false = a pre-auction LEAD. Always present on API responses; optional
  // here (not `| undefined` required) so older fixtures/tests that predate
  // this field keep compiling — consumers should treat a missing value as
  // `true` (today's status-only behavior) rather than crash.
  inAuction?: boolean;
  // Why this parcel is a lead — omitted by the API when empty. A parcel can
  // carry several signals at once (e.g. both a tax and mortgage
  // foreclosure filing); see lib/leadTypeMeta.ts for display priority.
  leadSignals?: LeadSignal[];
  // Timeshare resort property: one lead, many interval foreclosures. Always
  // sent by the API; optional here so older fixtures keep compiling.
  isTimeshare?: boolean;

  // ── typed raw_data accessors (#1872) ──────────────────────────────────
  //
  // These mirror backend/internal/handlers/property_view_fields.go. They
  // REPLACED the schemaless raw_data blob, which left the wire in #1872
  // (ADR 0035). The server does the string-vs-number coercion once
  // ("$84,357" arrives here as 84357) and applies the gate — a field absent
  // here for a free viewer is absent because they were not entitled to it,
  // not because the scraper never wrote it.
  //
  // Every one is optional: the API omits a group entirely when the property
  // has no data for it, so `building` being undefined means "no CAMA
  // enrichment", not "an empty object".
  //
  // Adding a field here that the Go struct does not send makes it silently
  // undefined at runtime; TestFrontendPropertyTypeMatchesPropertyView (Go,
  // internal/handlers) fails when that happens.
  portalCase?: PortalCaseView;
  noticeParties?: PartyView[];
  // Detail-only: every court case attached to this property when there are
  // two or more (a timeshare resort can carry hundreds). Absent on list payloads.
  linkedCases?: LinkedCasesView;
  building?: BuildingView;
  valuation?: ValuationView;
  situs?: SitusView;
  sale?: SaleView;
  /**
   * Owner of record + mailing address, from parcel_facts (#2076).
   *
   * Absent for TWO different reasons and the UI must not conflate them:
   * the viewer is not entitled (free, or outside a Scout plan's chosen
   * counties — viewProperty nils it), OR no parcel_facts row matched the
   * parcel (6 of the 196 live 21-day auctions, measured 2026-09-08).
   * Same ambiguity ADR 0035 documents for estMarketValue.
   */
  owner?: OwnerView;
  /** RFC3339. Newest of the portal/tyler/spatialest enrichment stamps. */
  lastVerifiedAt?: string;
  /** Scraper provenance tag — a fallback lead-type signal. */
  source?: string;
  /**
   * NC AOC case number from the foreclosure filing.
   *
   * NOT the same as `portalCase.caseNumber` — a row can carry this with no
   * Tyler portal case at all, and the synthetic `CASE:{n}` parcel id is minted
   * from this value.
   */
  courtFileNumber?: string;

  createdAt: string;
  updatedAt: string;
}

// ── typed raw_data groups (#1872) ───────────────────────────────────────
//
// Mirrors of the Go view structs in
// backend/internal/handlers/property_view_fields.go. Keep the two in step —
// the Go-side guard test enforces that this file never declares a field the
// server does not actually send.

/** One court-case or notice party with its role. */
/** The "court cases at this property" section (property detail only). */
export interface LinkedCasesView {
  /** Exact count; `cases` is capped (most recent sale first). */
  total: number;
  isTimeshare: boolean;
  cases: LinkedCaseView[];
}

export interface LinkedCaseView {
  courtFileNumber: string;
  /** Absent for viewers not entitled to party identities. */
  ownerName?: string;
  recordType: string;
  saleDate?: string;
  /** Timeshare interval, when the notice states it ("UNIT NO. 401 UNIT WEEK 42"). */
  unit?: string;
  week?: string;
}

export interface PartyView {
  name: string;
  /** "" for legacy rows scraped before the worker captured roles. */
  role: string;
}

/** One docket entry. Both fields are free-form as Tyler publishes them. */
export interface PortalActionView {
  date: string;
  description: string;
}

/**
 * Tyler court-case detail. Absent when the parcel has never been
 * portal-scraped.
 *
 * `parties` and `upsetBidders` are GATED — absent for a viewer whose plan
 * does not cover the county, even though the rest of the case is present.
 */
export interface PortalCaseView {
  caseNumber: string;
  caseStatus?: string;
  caseType?: string;
  fileDate?: string;
  nextHearing?: string;
  saleDate?: string;
  lastLookupAt?: string;
  actions?: PortalActionView[];
  parties?: PartyView[];
  upsetBidders?: PartyView[];
}

/** CAMA-sourced physical description of the parcel. */
export interface BuildingView {
  bedrooms?: number;
  fullBaths?: number;
  halfBaths?: number;
  totalSqft?: number;
  yearBuilt?: number;
  lotSizeAcres?: number;
  lotSizeSqft?: number;
  style?: string;
  heatingType?: string;
  foundation?: string;
  landUseDesc?: string;
  /** OneMap's explicit improved/vacant flag. */
  hasStructure?: boolean;
  /**
   * Number of buildings the source reports. 0 is a REAL answer (Polaris
   * returns an explicit empty array for vacant land) and is a stronger
   * vacancy signal than a missing sqft; undefined means the source does not
   * publish it at all. Do not conflate the two.
   */
  structureCount?: number;
}

/** CAMA land/building split. GATED in full. */
export interface ValuationView {
  landValue?: number;
  buildingValue?: number;
}

/**
 * Situs (physical) address a source derived for the parcel, as distinct from
 * `Property.address` — which for legal-description-only counties is deed text.
 */
export interface SitusView {
  address?: string;
  city?: string;
  zip?: string;
  /**
   * Deed text ("LT 28 KENSINGTON FOREST"). The address of LAST resort for
   * legal-description-only counties (Cabarrus) — render it flagged as a legal
   * description, never as if the filing stated a street address.
   */
  legalDescription?: string;
}

/**
 * Auction/sale figures published by the foreclosing firm. Bid AMOUNTS are
 * public record and not gated; only bidder identities are.
 */
export interface SaleView {
  openingBid?: number;
  currentBid?: number;
  originalPrincipalAmount?: number;
  saleTime?: string;
  saleLocation?: string;
  saleDate?: string;
  nodFiledDate?: string;
  /** End of the 10-day upset period — a sale is not final until this passes. */
  upsetWindowCloseAt?: string;
  /**
   * True when upsetWindowCloseAt was COMPUTED from the court docket (ten days
   * after the latest report of sale or upset bid, rolled past weekends and court
   * holidays) because no firm published a close date. The page labels it as an
   * estimate rather than presenting it as a published deadline.
   */
  upsetWindowDerived?: boolean;
  lastSaleText?: string;
  /**
   * Which raw_data key supplied `amountDue`. Provenance, not a figure — an
   * opening bid and an original deed-of-trust principal are very different
   * claims to put in front of a bidder.
   */
  amountDueSource?: string;
  /**
   * True when the sale is still `auction_scheduled` with an upcoming date but the
   * foreclosing firm's feed has stopped listing the property, so the date is
   * UNCONFIRMED (the sale may be cancelled, paused or resolved).
   */
  listingStale?: boolean;
  /** Consecutive healthy scrapes the firm's feed has missed it. Set with listingStale. */
  listingMissedRuns?: number;
}

// LeadSignal is one reason a property surfaced as a pre-auction lead (as
// opposed to a live auction). See lib/leadTypeMeta.ts for the priority order
// used to pick a single headline label when several signals are present.
export interface LeadSignal {
  signalType:
    | "tax_foreclosure"
    | "mortgage_foreclosure"
    | "foreclosure"
    | "probate"
    | "divorce"
    | "hoa_foreclosure"
    | "tax_delinquent"
    | "code_enforcement"
    | "condemnation"
    | "execution_sale"
    | "trustee_substitution";
  source: string;
  confidence: string;
  subStatus?: string;
  sourceRef?: string;
  signalDate?: string;
  /**
   * Specific human-readable violation class for a code_enforcement/
   * condemnation signal — "Repair order", "Minimum housing", "Condemnation /
   * demolition order" — computed backend-side by codeviolation.Class.Label()
   * (#2254). Absent for every other signalType, and absent for an older
   * code-violation row minted before this field existed. See
   * lib/dataQuality.ts resolvePropertyBadge for how the card/detail badge
   * uses this in place of the generic lead-type label.
   */
  violationLabel?: string;
  /**
   * Code-enforcement/condemnation evidence, so a user can verify the lead.
   * All four are present only for those two signal types and only when the
   * source recorded them; absent otherwise. None is owner or mailing data.
   * See lib/codeViolationEvidence.ts for how they are displayed.
   *
   * evidenceUrl: http(s) link to the record (or, failing that, the portal it
   * came from). The backend scheme-checks it (models.SafeEvidenceURL) and the
   * frontend checks again before rendering an href.
   */
  evidenceUrl?: string;
  /** Count of open cases on the parcel. Absent means unknown, NOT zero. */
  openCases?: number;
  /** Date of the most recent order/case/hearing the source reports (ISO). */
  orderDate?: string;
  /** Rollup escalation stage: notice | hearing | repair_order | demolition_order. */
  stage?: string;
}

// PropertyImage is the resolved best street-level photo for a property. For
// Google we carry the panorama id + heading and the FRONTEND builds the image
// URL with its own referrer-restricted key (the server never stores a
// key-bearing URL); for Mapillary the CDN `url` is directly displayable and
// must show `attribution` (CC BY-SA).
export interface PropertyImage {
  provider: string; // 'google_streetview' | 'mapillary'
  ref?: string;     // Street View pano_id
  url?: string;     // Mapillary CDN url
  heading?: number | null;
  captureDate?: string | null;
  attribution?: string;
}

// Lead is one pre-foreclosure seller lead from GET /api/v1/leads. Mirrors
// repositories.Lead on the backend. Nullable parcel-truth fields are
// `| null` because they come from OneMap/Cabarrus enrichment that may be
// missing or unknown (assessed value, absentee/entity signals).
export interface Lead {
  propertyId: string;
  countyId: string;
  parcelId: string;
  status: string;
  // inAuction: derived auction lens (status in the live-auction set). The Leads
  // table shows the auction status when true, the signalTypes label when false.
  inAuction: boolean;
  auctionDate: string | null;
  situsAddress: string;
  ownerName: string;
  mailingAddress: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  assessedValue: number | null;
  amountDue: number;
  equityEstimate: number | null;
  gisAcres: number | null;
  // yearsDelinquent: count of DISTINCT delinquent tax years, never a row count.
  // null means the parcel carries no delinquent-tax record at all, which is
  // NOT the same as 0 (current on taxes). Written by wake_tax_delinquent.go
  // and ncpts_delinquent.go; surfaced here for the first time.
  yearsDelinquent: number | null;
  isAbsentee: boolean | null;
  isEntityOwner: boolean | null;
  // isHeirProperty: the owner of record reads as an estate/heirs form
  // ("ESTATE OF ...", "HEIRS OF ...") rather than a living titleholder (#950).
  // Distinct outreach signal from absentee — usually multiple decision-makers
  // and an unclear chain of title. null until parcel_facts enrichment attaches.
  isHeirProperty: boolean | null;
  // signalTypes: WHY this parcel is a lead (tax_foreclosure / mortgage_foreclosure /
  // foreclosure / probate / divorce / hoa_foreclosure). One parcel, many signals.
  signalTypes: string[];
  lastSaleText: string;
  factsSource: string;
  // Added 2026-09-16: an investor-eyes audit of the Leads tab found these
  // already fetched for the property detail modal (property_view_fields.go)
  // but never reached this endpoint — the table showed owner/mailing/status/
  // equity and nothing about the physical asset or the legal case behind it.
  propertyType: string;
  propertyClass: string;
  courtFileNumber: string;
  bedrooms: number | null;
  fullBaths: number | null;
  halfBaths: number | null;
  totalSqft: number | null;
  yearBuilt: number | null;
  // lotSizeAcres: the CAMA-reported lot size — distinct from gisAcres above,
  // which is the GIS-measured parcel_facts figure. Can legitimately disagree.
  lotSizeAcres: number | null;
  landUseDesc: string;
  caseStatus: string;
  caseType: string;
  // lastSaleDate/lastSalePrice: structured parcel_sales data (most recent
  // sale) — distinct from lastSaleText above, which is OneMap's raw text.
  lastSaleDate: string | null;
  lastSalePrice: number | null;
  // highConfidenceOverlap (#2140): true when this lead carries BOTH an active
  // foreclosure-pipeline status (notice_of_default/notice_filed/
  // auction_scheduled/pending_confirmation/postponed) AND a code-violation-
  // family signal (code_enforcement/condemnation) — a filed proceeding against
  // the title plus adjudicated physical distress on the same parcel. Computed
  // server-side (repositories.highConfidenceOverlap) from status + signalTypes
  // already on this row, so it never disagrees with what's shown elsewhere.
  highConfidenceOverlap: boolean;
}

// BuyBox is a user's saved lead-filter set (GET/POST/PUT/DELETE /api/v1/buy-boxes).
// Mirrors backend models.BuyBox. Empty arrays = "any"; null numbers = "no bound".
export interface BuyBox {
  id: string;
  userId: string;
  name: string;
  counties: string[];
  signalTypes: string[];
  propertyClasses: string[];
  minAssessedValue: number | null;
  maxAssessedValue: number | null;
  minEquity: number | null;
  minAcres: number | null;
  maxAcres: number | null;
  absenteeOnly: boolean;
  entityOnly: boolean;
  // Building criteria (#3061, migration 247). includeUnknownBuilding keeps a
  // property whose value for a set criterion is missing (the default).
  minSqft: number | null;
  maxSqft: number | null;
  minYearBuilt: number | null;
  maxYearBuilt: number | null;
  minBeds: number | null;
  minBaths: number | null;
  includeUnknownBuilding: boolean;
  // Debt and land ranges (migration 263). Optional: older payloads omit them.
  minAmountDue?: number | null;
  maxAmountDue?: number | null;
  minLandValue?: number | null;
  maxLandValue?: number | null;
  // Email alerts for new matches (#3062). Optional: older payloads omit it.
  emailFrequency?: BuyBoxEmailFrequency;
  createdAt: string;
  updatedAt: string;
}

export type BuyBoxEmailFrequency = "off" | "daily" | "weekly";

// BuyBoxInput is the create/update body — the criteria without server-owned ids.
export interface BuyBoxInput {
  name: string;
  counties: string[];
  signalTypes: string[];
  propertyClasses: string[];
  minAssessedValue: number | null;
  maxAssessedValue: number | null;
  minEquity: number | null;
  minAcres: number | null;
  maxAcres: number | null;
  absenteeOnly: boolean;
  entityOnly: boolean;
  // Building criteria (#3061, migration 247). includeUnknownBuilding keeps a
  // property whose value for a set criterion is missing (the default).
  minSqft: number | null;
  maxSqft: number | null;
  minYearBuilt: number | null;
  maxYearBuilt: number | null;
  minBeds: number | null;
  minBaths: number | null;
  includeUnknownBuilding: boolean;
  minAmountDue: number | null;
  maxAmountDue: number | null;
  minLandValue: number | null;
  maxLandValue: number | null;
}

// NoticeLead is one statewide legal-notice lead from GET /api/v1/notice-leads
// (the flat breadth track: probate / foreclosure / hoa_foreclosure / divorce /
// tax_foreclosure). Mirrors backend repositories.NoticeLead. The parcel-fact
// enrichment fields (assessedValue/isAbsentee/isEntityOwner) are `| null` until
// a notice is matched to a parcel and enriched.
export interface NoticeLead {
  id: string;
  state: string;
  countyId: string;
  countyName: string;
  recordType: string;
  ownerName: string;
  party2Name: string;
  situsAddress: string;
  situsCity: string;
  situsZip: string;
  mailingAddress: string;
  mailingCity: string;
  mailingState: string;
  mailingZip: string;
  courtFileNumber: string;
  saleDate: string | null;
  noticeDate: string | null;
  detailUrl: string;
  source: string;
  assessedValue: number | null;
  isAbsentee: boolean | null;
  isEntityOwner: boolean | null;
  isHeirProperty: boolean | null;
}


export interface County {
  id: string;
  name: string;
  state: string;
  mapServerUrl: string;
  rules: Record<string, unknown>;
  fieldMappings: Record<string, string>;
  config?: CountyConfig;
  createdAt: string;
  updatedAt: string;
}

// CountyConfig is the type-safe view of `counties.config` JSONB.
// Mirrors `backend/internal/models/county_config.go` CountyConfig but
// only includes the fields the frontend actually reads. Backend has
// more (kania filter, polaris template, etc.) — not surfaced here
// because they're scraper-internal.
//
// IMPORTANT: keys are snake_case because the backend passes the raw
// JSONB through the API (no camelCase remap for nested config).
// Top-level `County` fields use camelCase via explicit JSON tags;
// `config` is the exception. Verified against `/api/v1/counties` live
// response 2026-05-02.
//
// All fields optional because new counties may be seeded before all
// config keys are populated; the consumer falls back to hardcoded
// defaults (e.g. COUNTY_VIEW_DEFAULTS in map.tsx) when missing.
export interface CountyConfig {
  active?: boolean;
  // map_viewport pins the initial map center+zoom when CountySelector
  // switches to this county. Backend writes via migration 022; frontend
  // reads in map.tsx. Falls back to the hardcoded COUNTY_VIEW_DEFAULTS
  // when unset (preserves backward compat for counties without DB-side
  // viewport set).
  map_viewport?: {
    center_lat: number;
    center_lon: number;
    zoom: number;
  };
}

/**
 * Owner of record and mailing address for a parcel (#2076). Mirrors the Go
 * OwnerView in backend/internal/handlers/property_view_fields.go.
 *
 * isAbsentee / isEntityOwner are OPTIONAL BOOLEANS, not booleans — undefined
 * means parcel_facts has not evaluated the flag, false means it did and the
 * owner lives there. Render the two differently; `!owner.isAbsentee` treats
 * "unchecked" as "owner-occupied", which is a confident claim about a fact
 * nobody has checked.
 */
export interface OwnerView {
  name?: string;
  name2?: string;
  mailingAddress?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  isAbsentee?: boolean;
  isEntityOwner?: boolean;
}
